import { unitInputSchema, unitUpdateSchema, type Unit } from "@cafegestor/shared";
import { useState, type FormEvent } from "react";
import { Button, Card, Field, Input, PageTitle, Select, Spinner, Table } from "@/components/ui";
import { useCan } from "@/app/me";
import { issuesToErrors, useCadastroList, useForm, useSave, type StatusFilter } from "./lib";
import { ListToolbar, Pager, SaveError, StatusBadge } from "./widgets";

export function UnitsPage() {
  const can = useCan();
  const canEdit = can("cadastros.produtos.gerenciar");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("active");
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<Unit | "new" | null>(null);
  const list = useCadastroList<Unit>("unidades", { q, status, offset });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Unidades de medida"
        description="Base para produtos e embalagens. O código não muda depois de criado."
        actions={
          canEdit && (
            <Button onClick={() => setEditing("new")} disabled={editing === "new"}>
              Nova unidade
            </Button>
          )
        }
      />
      {editing && (
        <UnitEditor
          key={editing === "new" ? "new" : editing.id}
          unit={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Card>
        <ListToolbar
          q={q}
          onQ={(v) => {
            setQ(v);
            setOffset(0);
          }}
          status={status}
          onStatus={(v) => {
            setStatus(v);
            setOffset(0);
          }}
        />
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Código", "Nome", "Casas decimais", "Situação", ""]}>
              {list.data?.items.map((u) => (
                <tr key={u.id}>
                  <td className="px-3 py-2 font-mono">{u.code}</td>
                  <td className="px-3 py-2">{u.name}</td>
                  <td className="px-3 py-2">{u.decimals}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(u)}>
                        Editar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function UnitEditor({ unit, onClose }: { unit: Unit | null; onClose: () => void }) {
  const f = useForm({
    code: unit?.code ?? "",
    name: unit?.name ?? "",
    decimals: String(unit?.decimals ?? 0),
    status: unit?.status ?? "active",
  });
  const save = useSave<unknown, Unit>({
    resource: "unidades",
    id: unit?.id ?? null,
    invalidate: ["unidades", "opcoes"],
    onSaved: onClose,
    successMessage: (_r, created) =>
      created ? "Unidade criada com sucesso." : "Unidade atualizada com sucesso.",
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = unit
      ? unitUpdateSchema.safeParse({ ...f.values, version: unit.version })
      : unitInputSchema.safeParse(f.values);
    if (!parsed.success) return f.setErrors(issuesToErrors(parsed.error.issues));
    f.setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <Card title={unit ? `Editar unidade ${unit.code}` : "Nova unidade"}>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-4">
        <Field label="Código" error={f.errors["code"]} hint="Ex.: KG, PCT, FD">
          <Input {...f.text("code")} disabled={Boolean(unit)} maxLength={6} />
        </Field>
        <Field label="Nome" error={f.errors["name"]} required>
          <Input {...f.text("name")} />
        </Field>
        <Field label="Casas decimais" error={f.errors["decimals"]} hint="0 para unidades inteiras">
          <Input type="number" min={0} max={6} {...f.text("decimals")} />
        </Field>
        <Field label="Situação">
          <Select {...f.text("status")}>
            <option value="active">Ativa</option>
            <option value="inactive">Inativa</option>
          </Select>
        </Field>
        <div className="flex gap-2 sm:col-span-4">
          <Button type="submit" disabled={save.isPending}>
            Salvar
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        <div className="sm:col-span-4">
          <SaveError error={save.error} />
        </div>
      </form>
    </Card>
  );
}
